/*
 * ICX Portal integration code
 */

import jwtDecode from 'jwt-decode';
import { throttle } from 'lodash';
import { isDevMode } from './index';
import { parse, stringify } from '../components/forked/stringify';

let portalMode = false;

const handleUpdateIccApiToken = eventData => {
  const icapitoken = eventData.data;
  const jwt = jwtDecode(icapitoken);

  if (jwt.client) {
    sessionStorage.setItem('token', icapitoken);
  }
};

const cleanupUrlAndRenderApp = (rest, callBack) => {
  let search = stringify(rest);
  if (search !== '') {
    search = '?' + search;
  }
  window.history.replaceState(
    {},
    'IC New Admin',
    window.location.pathname + search
  );

  portalMode = true;
  callBack();
};

const storeTenantInfo = (jwtClient, client) => {
  let isMupTenant = false;
  let effectiveClient = jwtClient;

  if (client) {
    sessionStorage.setItem('isMupChild', jwtClient !== client);
    isMupTenant = true;
    effectiveClient = client;
  }

  sessionStorage.setItem('isMupTenant', isMupTenant);
  sessionStorage.setItem('tenant', effectiveClient);
};

const handleSetIccApiToken = (eventData, callBack) => {
  const { client, ...rest } = parse(window.location.search);
  const icapitoken = eventData.data.icapitoken;

  if (!icapitoken) {
    return;
  }

  const jwt = jwtDecode(icapitoken);
  if (jwt.client) {
    sessionStorage.setItem('token', icapitoken);
    storeTenantInfo(jwt.client, client);
  }

  cleanupUrlAndRenderApp(rest, callBack);
};

const handleSetIccApiOauthToken = (eventData, callBack) => {
  const { client, ...rest } = parse(window.location.search);
  const icapitoken = eventData.data.icapitoken;
  let paramClient = eventData.data.client;

  if (!icapitoken) {
    return;
  }

  if (!paramClient) {
    return;
  }

  sessionStorage.setItem('token', icapitoken);
  sessionStorage.setItem('tokenType', 'bearer');

  if (client) {
    sessionStorage.setItem(
      'isMupChild',
      paramClient !== Buffer.from(client).toString('base64')
    );
    sessionStorage.setItem('isMupTenant', true);
    paramClient = client;
  } else {
    sessionStorage.setItem('isMupTenant', false);
  }

  sessionStorage.setItem('tenant', paramClient);

  cleanupUrlAndRenderApp(rest, callBack);
};

const handleUpdateIccApiOauthToken = eventData => {
  sessionStorage.setItem('token', eventData.data);
  sessionStorage.setItem('tokenType', 'bearer');
};

/**
 * ICX message handler (posted from ICX portal to iFrame)
 * @param {*} eventData
 * @param callBack
 */
const receiveMessageFromIcx = (eventData, callBack) => {
  const actionHandlers = {
    updateIccApiToken: () => handleUpdateIccApiToken(eventData),
    setIccApiToken: () => handleSetIccApiToken(eventData, callBack),
    setIccApiOauthToken: () => handleSetIccApiOauthToken(eventData, callBack),
    updateIccApiOauthToken: () => handleUpdateIccApiOauthToken(eventData)
  };

  const handler = actionHandlers[eventData.action];
  if (handler) {
    handler();
  }
};

/**
 * Initialize ICX Portal communication
 */
export const initIcxPortal = callBack => {
  // Handle frame messages posted from ICX Portal
  window.addEventListener(
    // NOSONAR
    'message',
    messageEvent => {
      if (typeof messageEvent.data === 'string') {
        try {
          const eventContent = JSON.parse(messageEvent.data);
          if (eventContent.origin !== 'ICX') return;

          if (eventContent.origin === 'ICX') {
            receiveMessageFromIcx(eventContent, callBack);
          }
        } catch (error) {
          // Do nothing, not a JSON message
        }
      }
    },
    false
  );

  if (isDevMode()) {
    // Dev mode is on -> just render the application
    callBack();
  } else {
    // Dev mode is off, accessing through Portal -> send message to ICX Portal to receive the token
    sendgetIccApiTokenToIcx();
  }

  const sendPingOnMoveThrottled = throttle(sendPingOnMove, 60000);
  window.addEventListener('mousemove', sendPingOnMoveThrottled);
};

const sendPingOnMove = () => {
  sendPingToIcx();
};

/**
 * Check if running in ICX Portal
 */
export const runningInPortal = () => {
  return portalMode;
};

/**
 * Send Ping to ICX Portal
 */
let pingTimeout = undefined;
const PING_TIMEOUT = 60000;

export const sendPingToIcx = () => {
  // 1st test if a sending ping has been forecasted
  if (portalMode && pingTimeout === undefined) {
    // No ping forecasted, let's send it in 1 minute
    pingTimeout = setTimeout(() => {
      pingTimeout = undefined;

      var parentDomain = document.location.ancestorOrigins
        ? document.location.ancestorOrigins[0]
        : document.referrer;
      if (parentDomain === undefined || parentDomain === '') parentDomain = '*';

      // SonarQube: we must send this ping message to our container. No sensitive info sent !
      const msg = '{ "origin": "IcxAdmin", "action": "ping"}';
      window.parent.postMessage(msg, parentDomain); // NOSONAR
    }, PING_TIMEOUT);
  }
};

export const sendgetIccApiTokenToIcx = () => {
  let parentDomain = document.location.ancestorOrigins
    ? document.location.ancestorOrigins[0]
    : document.referrer;
  if (parentDomain === undefined || parentDomain === '') parentDomain = '*';

  // SonarQube: we must send getIccApiToken message to our container so we can get the token from ICX Portal. No sensitive info sent !
  const msg = '{ "origin": "IcxAdmin", "action": "getIccApiToken"}';
  window.parent.postMessage(msg, parentDomain); // NOSONAR
};
